import { useMemo, useState } from 'react';
import { AnalystButton } from '../components/Analyst/AnalystButton';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { SpecialtyRow } from '../api/types.js';
import { OfferingCards } from '../components/OfferingCards/OfferingCards.js';
import { RegionStrip } from '../components/RegionStrip/RegionStrip.js';
import { SegmentedControl } from '../components/SegmentedControl/SegmentedControl.js';
import {
  SpecialtyTable,
  specialtyRowKey,
} from '../components/SpecialtyTable/SpecialtyTable.js';
import {
  EmptyState,
  ErrorBanner,
  NoSnapshotState,
  Skeleton,
} from '../components/States/States.js';
import { queries } from '../store/queries.js';
import { useQuery } from '../store/StoreProvider.js';
import { Header } from '../shell/Header.js';
import { color, fontSize, isUiRegion, radius, space, type UiRegion } from '../theme/tokens.js';

/**
 * Specialties — the clinical view (FR-S01 – FR-S35, PRD-Specialties §6).
 *
 * **One page, two states.** Nothing selected, or one discipline selected. Selecting a row does
 * *not* replace the table: the row highlights and the header and region strip re-scope, because
 * the value of the page is comparison and replacing the comparison set to inspect one member of
 * it destroys that (FR-S24).
 *
 * ```text
 * /specialties                                    every discipline
 * /specialties/:professionKey/:specialtyKey        that discipline selected
 * ?region=R3                                      on either, scoped to one region
 * ```
 *
 * ⚠ **This page has no time dimension.** No chart, no period control, no source/targeting
 * filters, no trend arrows — every figure is an as-of snapshot (FR-S13 – FR-S16). The blueprint's
 * Specialties section has none of them either, and it is coherent *because* of that: adding any
 * one would put an all-time figure beside a last-quarter figure in the same row.
 */

/** URL-safe slug, so a discipline can be linked to and reopened (FR-S25). */
const slug = (value: string): string => encodeURIComponent(value);

type OfferingFilter = 'all' | 'nursing' | 'allied';

export function Specialties() {
  const { professionKey, specialtyKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const regionParam = searchParams.get('region');
  // An unrecognised `?region=` is ignored rather than refused — the page's default scope is every
  // region, so a stale link (`?region=SPA` since 2026-09-02) lands on the whole book, not an error.
  const region: UiRegion | null = isUiRegion(regionParam) ? regionParam : null;

  const [offering, setOffering] = useState<OfferingFilter>('all');

  const state = useQuery(queries.specialties(region));

  const selectedKey =
    professionKey && specialtyKey
      ? `${decodeURIComponent(professionKey)}|${decodeURIComponent(specialtyKey)}`
      : null;

  const data = state.data?.data ?? null;

  /**
   * The offering toggle filters **which rows render** and recomputes nothing (FR-S21).
   *
   * It deliberately does not touch the summary cards or the region strip: those are the fixed
   * reference the rows are compared against.
   */
  const visibleRowSet = useMemo<SpecialtyRow[]>(() => {
    if (!data) return [];
    if (offering === 'all') return data.rows;
    return data.rows.filter((r) => r.offering === offering);
  }, [data, offering]);

  const selectedRow = useMemo(
    () => visibleRowSet.find((r) => specialtyRowKey(r) === selectedKey) ?? null,
    [visibleRowSet, selectedKey],
  );

  /**
   * With a discipline selected the chips show **that discipline's** figures. No request is made —
   * the row already carries its own `byRegion`, which is why this interaction is instant.
   */
  const chips = useMemo(() => {
    if (!data) return [];
    if (!selectedRow) return data.regions;
    return data.regions.map((chip) => ({
      ...chip,
      bob: selectedRow.byRegion[chip.code]?.bob ?? null,
      activeToa: selectedRow.byRegion[chip.code]?.activeToa ?? null,
    }));
  }, [data, selectedRow]);

  function selectRow(row: SpecialtyRow) {
    const key = specialtyRowKey(row);
    const query = region ? `?region=${region}` : '';
    // Selecting the active row clears — the same toggle the region chips use.
    if (key === selectedKey) navigate(`/specialties${query}`);
    else navigate(`/specialties/${slug(row.profession)}/${slug(row.specialty)}${query}`);
  }

  /** `null` is the **All** chip. Choosing the already-active region clears it the same way. */
  function selectRegion(code: UiRegion | null) {
    const next = new URLSearchParams(searchParams);
    if (code === null || code === region) next.delete('region');
    else next.set('region', code);
    setSearchParams(next, { replace: false });
  }

  const contextLabel = [
    selectedRow ? `${selectedRow.profession} — ${selectedRow.specialty}` : null,
    region ? (chips.find((c) => c.code === region)?.name ?? region) : 'All regions',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Header
        crumb="CYNET Health Commercial"
        title="Specialties"
        asOf={state.data?.asOf ?? null}
        syncedAt={state.data?.syncedAt ?? null}
        syncStatus={state.data?.syncStatus ?? 'no_data'}
        staleness={state.data?.staleness ?? null}
        unattributedBooks={state.data?.dataQuality?.unattributedBooks}
        actions={<AnalystButton />}
      />

      <div style={{ padding: `${space['6xl']} ${space['8xl']}` }}>
        {state.loading && <Skeleton height={420} />}
        {state.error && <ErrorBanner message={state.error.message} onRetry={state.reload} />}
        {!state.loading && !state.error && state.data?.syncStatus === 'no_data' && (
          <NoSnapshotState />
        )}

        {data && (
          <>
            {/* ---- 1. Region scope — the page's first control ----

                Above the cards (owner request 2026-08-28), where PRD-Specialties puts it inside
                the table card below them. It belongs here on the numbers as much as on the
                layout: the region scope is a **request parameter**, so it re-scopes the whole
                response — the two offering cards included (`api/specialties.ts`, FR-S21). Sitting
                under the cards it moved, it read as a control over the table alone. */}
            <div
              style={{
                background: color.surface,
                border: `1px solid ${color.border}`,
                borderRadius: radius['6xl'],
                marginBottom: space['4xl'],
              }}
            >
              <RegionStrip
                chips={chips}
                selected={region}
                onSelect={selectRegion}
                scopeLabel={
                  selectedRow ? `${selectedRow.profession} — ${selectedRow.specialty}` : null
                }
              />
            </div>

            {/* ---- 2. The two offering cards — region-scoped, filter-independent ---- */}
            <OfferingCards nursing={data.cards.nursing} allied={data.cards.allied} />

            {/* ---- 3. The comparison table ---- */}
            <section
              style={{
                background: color.surface,
                border: `1px solid ${color.border}`,
                borderRadius: radius['6xl'],
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: space['3xl'],
                  padding: `${space['4xl']} ${space['5xl']}`,
                  borderBottom: `1px solid ${color.borderSubtle}`,
                }}
              >
                <h2 style={{ margin: 0, fontSize: fontSize.bodyStrong, fontWeight: 600 }}>
                  Specialties
                </h2>
                <span style={{ fontSize: fontSize.caption, color: color.inkMuted }}>
                  {contextLabel}
                </span>
                <div style={{ marginLeft: 'auto' }}>
                  <SegmentedControl
                    options={[
                      { value: 'all' as OfferingFilter, label: 'All' },
                      { value: 'nursing' as OfferingFilter, label: 'Nursing' },
                      { value: 'allied' as OfferingFilter, label: 'Allied' },
                    ]}
                    value={offering}
                    onChange={setOffering}
                    label="Offering"
                  />
                </div>
              </div>

              {visibleRowSet.length === 0 ? (
                <EmptyState title="No specialties match this filter." />
              ) : (
                <SpecialtyTable
                  rows={visibleRowSet}
                  rowCount={visibleRowSet.length}
                  selectedKey={selectedKey}
                  onSelect={selectRow}
                />
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
